import { useCallback, useEffect, useMemo } from "react"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { useNavigate, useParams } from "react-router"
import { ChatThreadLayout } from "@/modules/chats/components/chat-layout/chat-thread-layout"
import { ChatRequestTakeOverDialog } from "@/modules/chats/components/chat-request-take-over-dialog"
import { getChatThreadByChatAppointment } from "@/modules/chats/lib/get-chat-thread-by-chat-appointment"
import { getChatRoute } from "@/modules/chats/lib/routing-helpers"
import { useChatAppointmentsQuery } from "@/modules/chats/services/__generated__/useChatAppointmentsQuery.graphql"
import { useOnChatChangedSubscription$data } from "@/modules/chats/services/__generated__/useOnChatChangedSubscription.graphql"
import { chatAppointmentsQuery } from "@/modules/chats/services/useChatAppointmentsQuery"
import { useMarkAllThreadMessagesAsReadMutation } from "@/modules/chats/services/useMarkAllThreadMessagesAsReadMutation"
import { useMyChatAssignmentChangedSubscription } from "@/modules/chats/services/useMyChatAssignmentChangedSubscription"
import { useOnChatChangedSubscription } from "@/modules/chats/services/useOnChatChangedSubscription"
import { useChatThreadContext, useChatThreadStore } from "@/modules/chats/stores/useChatThreadStore"
import { useUnsentMessageContext } from "@/modules/chats/stores/useUnsentMessageStore"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"

type ChatResizableLayoutContentProps = {
  queryReference: PreloadedQuery<useChatAppointmentsQuery>
}

export const ChatResizableLayoutContent = (props: ChatResizableLayoutContentProps) => {
  const { queryReference } = props

  const { threadId } = useParams()

  const navigate = useNavigate()
  const deleteProgressNoteFormByAppointmentId = useProgressNoteFormContext(
    (state) => state.deleteProgressNoteFormByAppointmentId,
  )
  const setUnsentMessage = useUnsentMessageContext((state) => state.setUnsentMessage)
  const chatThreadStore = useChatThreadStore()
  const setSelectedThread = useChatThreadContext((state) => state.setSelectedThread)
  const [markAllThreadMessagesAsRead] = useMarkAllThreadMessagesAsReadMutation()

  const { chatsTakenByMe } = usePreloadedQuery<useChatAppointmentsQuery>(chatAppointmentsQuery, queryReference)
  const chatAppointmentEdges = useMemo(() => chatsTakenByMe?.edges ?? [], [chatsTakenByMe?.edges])

  const handleInboundMessage = useCallback(
    (data: useOnChatChangedSubscription$data | null | undefined) => {
      const receivedMessage = data?.onChatChanged.receivedMessage
      if (!receivedMessage) return

      const { threadId: incomingThreadId, senderKind } = receivedMessage
      const isThreadOpen = chatThreadStore.getState().selectedThread?.id === incomingThreadId
      if (senderKind === "PATIENT" && isThreadOpen) {
        markAllThreadMessagesAsRead({ variables: { input: { threadId: incomingThreadId } } })
      }
    },
    [chatThreadStore, markAllThreadMessagesAsRead],
  )
  useOnChatChangedSubscription(handleInboundMessage)

  const handleThreadRemoved = useCallback(
    (chatThreadId: string, appointmentId: string) => {
      setUnsentMessage(chatThreadId, null)
      deleteProgressNoteFormByAppointmentId(appointmentId)
    },
    [setUnsentMessage, deleteProgressNoteFormByAppointmentId],
  )

  useMyChatAssignmentChangedSubscription(handleThreadRemoved)

  useEffect(() => {
    if (!threadId) {
      setSelectedThread(undefined)
      return
    }

    const matchingEdge = chatAppointmentEdges.find((edge) => edge.node.chat?.threadId === threadId)
    if (matchingEdge) {
      setSelectedThread(getChatThreadByChatAppointment(matchingEdge.node))
    } else {
      navigate(getChatRoute(), { replace: true })
    }
  }, [threadId, chatAppointmentEdges, navigate, setSelectedThread])

  return (
    <>
      <ChatThreadLayout chatAppointmentEdges={chatAppointmentEdges} />
      <ChatRequestTakeOverDialog />
    </>
  )
}
